import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { emptyObject } from 'ui-components/default-props/empty';
import Global from '../../../../store/global-selectors';
import { CopyToClipboardButton } from 'ui-components/components/copy-to-clipboard-button/copy-to-clipboard-button';
import { Text, TextType } from 'ui-components/components/text';
import { FlexRow } from 'ui-components/components/flex';
import messages from '../messages';
import style from './printing-error.module.less';

const extractErrorProperties = (error) => {
  if (typeof error !== 'object') {
    return 'There was an error parsing the object.';
  }
  return Object.entries(error)
    ?.map(([key, value]) => `${key}: ${value}`)
    .join('\r\n');
};

const getTextToCopy = ({ error, errorMessage, requestId, workstation }) => {
  const text = `
printJobId: ${requestId}
workstationId: ${workstation?.workstationId}
workstationVersion: ${workstation?.version}
---------
displayMessage: ${errorMessage}
${extractErrorProperties(error)}
`;
  return text;
};

export const PrintingErrorNotification = ({ error = emptyObject, errorMessage, requestId, workstationId }) => {
  const cleanError = { ...error };
  // remove properties that shouldn't be exposed
  delete cleanError?.stack;
  delete cleanError?.config; // axios errors contain this property which is noise

  const workstation = useSelector((state) => Global.selectConnectWorkstationById(state, { workstationId }));
  const errorTextToCopy = getTextToCopy({
    error: cleanError,
    errorMessage,
    requestId,
    workstation,
  });

  return (
    <div>
      <Text
        className={style.bold}
        formattedMessageValues={{ printJobError: errorMessage }}
        textType={TextType.Paragraph}
        value={messages.printingErrorContent}
      />
      <Text
        formattedMessageValues={{
          workstationName: workstation?.description ?? '',
        }}
        textType={TextType.Paragraph}
        value={messages.printingErrorWorkstationIdentification}
      />
      <FlexRow className={style['error-block']}>
        <FlexRow className={style['clipboard-button-container']}>
          <CopyToClipboardButton
            textToCopy={errorTextToCopy}
            tooltipMessageDescriptor={messages.printingErrorCopyToClipboardAriaLabel}
            tooltipPosition='top-right'
          />
        </FlexRow>
        <ul className={style['error-values']}>
          {Object.entries(cleanError)?.map(([key, value]) => {
            const displayValue = typeof value === 'string' ? value : JSON.stringify(value);
            return (
              <li key={key} className={style['error-property']}>
                <span className={style['error-key']}>{key.toUpperCase()}:</span>
                <span className={style['error-value']}>{displayValue}</span>
              </li>
            );
          })}
        </ul>
      </FlexRow>
    </div>
  );
};

PrintingErrorNotification.propTypes = {
  errorMessage: PropTypes.string.isRequired,
  error: PropTypes.object,
  requestId: PropTypes.string.isRequired,
  workstationId: PropTypes.string.isRequired,
};
